import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

const SECTIONS = [
  {
    title: "1. Purpose",
    body: "This Data Processing Agreement (DPA) forms part of your use of Savy Kerno and applies where we process personal data on your behalf — primarily the customer data you store in the CRM, appointment records, call records and transcripts, and marketing content.",
  },
  {
    title: "2. Roles",
    body: "You (the business customer) are the data controller. Savy Kerno is the data processor, processing personal data only on your documented instructions as given through your use of the product's features.",
  },
  {
    title: "3. What we process",
    body: "Contact details and notes for your leads and customers; appointment details; call times, numbers, outcomes and transcripts; marketing drafts you approve; and the business profile you configure. We do not process special-category data, and you must not add it to the platform.",
  },
  {
    title: "4. How we protect it",
    body: "Tenant isolation is enforced on every request and covered by automated tests; passwords are bcrypt-hashed; access uses signed, expiring tokens; security events are logged with hashed IP addresses; provider credentials are stored server-side. No method is perfectly secure — if a breach affects your data we will inform you without undue delay.",
  },
  {
    title: "5. Subprocessors",
    body: "We use a small number of subprocessors to run the service: cloud hosting (Render), managed database hosting (MongoDB Atlas), AI processing (OpenAI API), payments (Stripe), and — only where you connect them — email sending, telephony and social platforms. AI API processing is not used to train provider models. We do not claim certifications we do not hold; ask us for the current list at any time.",
  },
  {
    title: "6. International transfers",
    body: "Some subprocessors process data outside the UK. Where they do, transfers rely on the provider's UK GDPR transfer safeguards (such as the UK Addendum to EU standard contractual clauses).",
  },
  {
    title: "7. Retention and deletion",
    body: "We keep personal data while your account is active. You can delete leads, appointments, calls and content at any time in the dashboard. On account deletion, remaining personal data is removed within 30 days except where we must retain billing or security records for legal, tax or fraud-prevention reasons.",
  },
  {
    title: "8. Your obligations",
    body: "You must have a lawful basis for the customer data you upload (including consent where required for calls or marketing), honour opt-outs, keep your account credentials safe, and respond to your own customers' data rights requests. CSV imports never grant marketing consent by default — you are responsible for recording genuine consent before using those columns.",
  },
  {
    title: "9. Data subject requests",
    body: "If one of your customers exercises their rights (access, correction, deletion, objection), the dashboard lets you export or delete their records. Email support@savykernoai.com if you need help; we will assist within the legally required period.",
  },
  {
    title: "10. Review status",
    body: "This DPA is a working draft pending qualified legal review before paid launch. It is not a substitute for legal advice and creates no warranties beyond those in the Terms of Service.",
  },
];

export default function DataProcessing() {
  return (
    <div className="min-h-screen bg-white" data-testid="dpa-page">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#111111]">
          <BrandLogo size={22} /> <span className="font-semibold">Savy Kerno</span>
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Data Processing Agreement</h1>
        <p className="mt-3 text-sm text-neutral-500">Last updated November 2026 · Draft pending legal review</p>
        <div className="mt-8 space-y-6">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-bold text-[#111111]">{s.title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-12 flex gap-4 border-t border-neutral-100 pt-6 text-xs text-neutral-400">
          <Link to="/privacy" className="hover:text-[#111111]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#111111]">Terms of Service</Link>
          <Link to="/ai-calling-policy" className="hover:text-[#111111]">AI Calling & Recording</Link>
          <Link to="/cookies" className="hover:text-[#111111]">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
